import { Dispatch, ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import * as fabric from "fabric";
import { editorInterface } from "store/slice/editor";
import deleteIcon from "../../assets/images/trash.png";
import store from "../../store";
import { FabricImage } from "fabric";

/**
 * Creates a debounced function that delays the execution of the provided function
 * until after the specified delay has elapsed since the last time the debounced
 * function was invoked.
 *
 * @param {Function} fn - The function to debounce. It accepts any number of arguments.
 * @param {number} delay - The number of milliseconds to delay the function execution.
 * @returns {Function} A new debounced function that will delay the execution of the
 * original function until after the delay has elapsed since the last call.
 */
const debounce = (fn: (...args: any[]) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

/**
 * Updates the signature state in the editor with new properties and dispatches the update.
 *
 * @function
 * @name dispatchSignatureUpdate
 * @param {string} src - The base64 encoded string representation of the signature image.
 * @param {Object} signature - The signature object containing its properties and transformations.
 * @param {string} selectedLayout - The currently selected layout of the editor.
 * @param {string} color - The color associated with the signature.
 * @param {Function} dispatch - The dispatch function used to update the editor state.
 * @param canvas
 */
const dispatchSignatureUpdate = ( src, signature, selectedLayout, color, dispatch, canvas ) => {
  const scaledWidth = signature.getScaledWidth();
  const scaledHeight = signature.getScaledHeight();

  const positionInPercentage = {
    x: (signature.left / canvas.width) * 100,
    y: (signature.top / canvas.height) * 100
  };

  dispatch( {
    type: "editor/setBase64Signature",
    payload: {
      base64: src,
      color: color,
      layout: selectedLayout,
      position: {
        x: positionInPercentage.x,
        y: positionInPercentage.y
      },
      size: {
        width: scaledWidth,
        height: scaledHeight,
      },
      angle: signature.angle
    }
  } );
  };

/**
 * Dispatches an action to remove a signature from a specified layout in the editor.
 *
 * @param {string} src - The base64 string representation of the signature to be removed.
 * @param {string} selectedLayout - The layout identifier where the signature resides.
 * @param {Function} dispatch - The Redux dispatch function to dispatch actions.
 */
const dispatchSignatureRemove = ( src, selectedLayout,dispatch ) => {
    dispatch( {
    type: "editor/removeSignatureFromLayout",
    payload: {
      base64: src,
      layout: selectedLayout,
    }
  } );
};



/**
 * Creates a canvas signature element and adds it to the provided canvas.
 *
 * @param signatureBase64 - The base64-encoded image data for the signature.
 * @param color
 * @param canvas - The canvas element to add the signature to.
 * @param dispatch
 * @param selectedLayout
 * @param signature
 * @returns A Promise that resolves with the created signature object.
 */
export const createCanvasSignature = ( signatureBase64: string,
  color: string = "#000",
  canvas: fabric.Canvas,
  dispatch: ThunkDispatch<{ editor: editorInterface; }, undefined, UnknownAction> & Dispatch<UnknownAction>,
  selectedLayout: number,
  signature?: Signature
) => {
  const getScale = () => store.getState().editor.scale
const scale = getScale()

  return new Promise((resolve, reject) => {
    try {
      const fabricSignature = new Image();

      fabricSignature.src = signatureBase64;

      const renderDeleteIcon = (ctx: CanvasRenderingContext2D, left: number, top: number, fabricObject: fabric.Object) => {
        const size = 24;
        const img = new Image();

        const padding = 6;
        const borderRadius = 8;

        img.src = deleteIcon;

        ctx.save();
        ctx.translate(left, top);
        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle));

        const rectSize = size + padding * 2;
        ctx.beginPath();
        ctx.moveTo(-rectSize / 2 + borderRadius, -rectSize / 2);
        ctx.arcTo(rectSize / 2, -rectSize / 2, rectSize / 2, rectSize / 2, borderRadius);
        ctx.arcTo(rectSize / 2, rectSize / 2, -rectSize / 2, rectSize / 2, borderRadius);
        ctx.arcTo(-rectSize / 2, rectSize / 2, -rectSize / 2, -rectSize / 2, borderRadius);
        ctx.arcTo(-rectSize / 2, -rectSize / 2, rectSize / 2, -rectSize / 2, borderRadius);
        ctx.closePath();

        ctx.fillStyle = "white";
        ctx.fill();

        ctx.drawImage(img, -size / 2, -size / 2, size, size);
        ctx.restore();
        canvas.requestRenderAll();
      };

      const deleteObject = () => {
        const activeObject = canvas.getActiveObject();

        if ( activeObject ) {
          const base64 = activeObject.get("base64");
          canvas.remove( activeObject );
          canvas.discardActiveObject();
          canvas.requestRenderAll();
          if (base64) {
            dispatchSignatureRemove(base64,selectedLayout, dispatch );
          }
        }
        return true;
      };

      const signatureWidth = 400;
      const signatureHeight = 200;

      fabricSignature.onload = () => {

        const tempCanvas = document.createElement( "canvas" );
        tempCanvas.width = signatureWidth;
        tempCanvas.height = signatureHeight;
        const ctx = tempCanvas.getContext( "2d" );

        if (ctx) {

        ctx.drawImage( fabricSignature, 0, 0, signatureWidth, signatureHeight );

        ctx.globalCompositeOperation = "source-in";
        ctx.fillStyle = color;
        ctx.fillRect( 0, 0, signatureWidth, signatureHeight );
        }

        const left = signature?.position.x ? (signature?.position.x / 100) * canvas.width : canvas.width / 2;
        const top = signature?.position.y ? (signature?.position.y / 100) * canvas.height : (canvas.height / 4) * 3;
        const angle = signature?.angle ?? 0;
        const base64 = signature?.base64 ?? ""

        const coloredSignature = new FabricImage( tempCanvas, {
          left,
          top,
          scaleX: scale,
          scaleY: scale,
          originX: "center",
          originY: "center",
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
          hasControls: true,
          hasBorders: true,
          hoverCursor: "move",
          angle,
          base64: base64,
        } );


        coloredSignature.controls.deleteControl = new fabric.Control( {
          x: 0.5,
          y: -0.5,
          offsetY: -16,
          offsetX: 16,
          sizeX: 40,
          sizeY: 40,
          cursorStyle: "pointer",
          render: renderDeleteIcon,
          mouseUpHandler: deleteObject,
        } );

        if ( canvas ) {
          canvas.add( coloredSignature );
          canvas.renderAll();
          canvas.requestRenderAll();
        }


        const debouncedDispatchUpdate = debounce(
          (src, signature, selectedLayout, color, dispatch) => {
            dispatchSignatureUpdate( src, signature, selectedLayout, color, dispatch, canvas );
          },
          300
        );

        coloredSignature.on("moving", () => {
          coloredSignature.setCoords();
          debouncedDispatchUpdate(fabricSignature.src, coloredSignature, selectedLayout, color, dispatch);
        });

        coloredSignature.on("mouseup", () => {

          const canvasWidth = canvas.width;
          const canvasHeight = canvas.height;

          if (coloredSignature.left * scale < coloredSignature.width * scale / 2) coloredSignature.left = coloredSignature.width * scale / 2 + 30;
          if (coloredSignature.top * scale < coloredSignature.height * scale / 2) coloredSignature.top = coloredSignature.height * scale / 2 + 15;
          if (coloredSignature.left * scale + coloredSignature.width * scale > canvasWidth) coloredSignature.left = canvasWidth - coloredSignature.width * scale / 2 - 30;
          if (coloredSignature.top * scale + coloredSignature.height * scale > canvasHeight) coloredSignature.top = canvasHeight - coloredSignature.height * scale / 2 - 15;

          coloredSignature.setCoords();
          canvas.requestRenderAll();
        });

        coloredSignature.on("scaling", () => {
          coloredSignature.setCoords();
          debouncedDispatchUpdate(fabricSignature.src, coloredSignature, selectedLayout, color, dispatch);
        });

        coloredSignature.on("rotating", () => {
          coloredSignature.setCoords();
          debouncedDispatchUpdate(fabricSignature.src, coloredSignature, selectedLayout, color, dispatch);
        });

        dispatchSignatureUpdate( fabricSignature.src, coloredSignature, selectedLayout, color, dispatch, canvas );
        resolve( coloredSignature );
      }
    } catch (error) {
      console.log("Error creating signature", error);
      reject(error);
    }
  });
};